import { useState } from 'react';
import JsonView from '@uiw/react-json-view';
import { lightTheme } from '@uiw/react-json-view/light';
import { darkTheme } from '@uiw/react-json-view/dark';
import { Button } from '@/components/ui/button';
import { Copy, Check, ChevronsUpDown, ChevronsDownUp, Download } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { downloadAsJSON } from '@/lib/utils';

const JSONViewer: React.FC<{
  data: unknown;
  downloadName?: string;
  defaultExpanded?: boolean;
  collapsedHeight?: number;
  expandedHeight?: number;
}> = ({
  data,
  downloadName,
  defaultExpanded = false,
  collapsedHeight = 500,
  expandedHeight = 800,
}) => {
  const [copied, setCopied] = useState(false);
  const [collapseDepth, setCollapseDepth] = useState<number | false>(1);
  const [remountKey, setRemountKey] = useState(0);
  const { theme } = useTheme();

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const expandAll = () => {
    setCollapseDepth(false);
    setRemountKey((k) => k + 1);
  };

  const collapseAll = () => {
    setCollapseDepth(1);
    setRemountKey((k) => k + 1);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={handleCopy}>
          {copied ? (
            <>
              <Check className="size-4 mr-1 text-green-600" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="size-4 mr-1" />
              Copy JSON
            </>
          )}
        </Button>
        <Button variant="outline" size="sm" onClick={expandAll}>
          <ChevronsUpDown className="size-4 mr-1" />
          Expand All
        </Button>
        <Button variant="outline" size="sm" onClick={collapseAll}>
          <ChevronsDownUp className="size-4 mr-1" />
          Collapse All
        </Button>
        {!!downloadName && (
          <Button variant="outline" size="sm" onClick={() => downloadAsJSON(data, downloadName)}>
            <Download className="size-4 mr-1" />
            Download
          </Button>
        )}
      </div>

      <div
        className="border border-border rounded-md bg-muted/30 overflow-auto"
        style={{ maxHeight: defaultExpanded ? expandedHeight : collapsedHeight }}
      >
        <div className="p-4">
          <JsonView
            key={remountKey}
            value={(data as object) ?? {}}
            collapsed={collapseDepth}
            displayDataTypes={false}
            enableClipboard={false}
            style={(theme === 'dark' ? darkTheme : lightTheme) as React.CSSProperties}
          />
        </div>
      </div>
    </div>
  );
};

export default JSONViewer;
